import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, LogOut, RefreshCw, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OperatorPanel = ({ isOpen, onClose }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [usuario, setUsuario] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [loginError, setLoginError] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/operator/waitlist`);
      const data = await res.json();
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (loggedIn && isOpen) fetchEntries();
  }, [loggedIn, isOpen, fetchEntries]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/operator/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario, contrasena }),
      });
      if (!res.ok) throw new Error('Credenciales inválidas');
      setLoggedIn(true);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setUsuario('');
    setContrasena('');
    onClose();
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/operator/waitlist/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    setEntries(prev => prev.map(e => e.id === id ? { ...e, status } : e));
  };

  const updateNotes = async (id, notas) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, notas } : e));
    await fetch(`${API_URL}/api/operator/waitlist/${id}/notes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notas }),
    });
  };

  const exportCSV = () => {
    window.open(`${API_URL}/api/operator/waitlist/export`, '_blank');
  };

  const filtered = entries.filter(e =>
    !searchTerm ||
    e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.correo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.ciudad?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  const panelStyle = {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: '#F5F5F7',
    display: 'flex', flexDirection: 'column',
    fontFamily: 'Inter, sans-serif',
    overflow: 'hidden',
  };

  if (!loggedIn) {
    return (
      <div style={panelStyle} data-testid="operator-login-screen">
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <form onSubmit={handleLogin} style={{
            width: '100%', maxWidth: '360px', padding: '0 24px',
            display: 'flex', flexDirection: 'column', gap: '16px',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', letterSpacing: '0.2em', color: '#0A0A0A', marginBottom: '8px' }}>
                ETERNA
              </div>
              <div style={{ fontWeight: 300, fontSize: '14px', color: '#6B6B6B' }}>
                Panel de Operador
              </div>
            </div>

            <input
              type="text"
              placeholder="Usuario"
              value={usuario}
              onChange={e => setUsuario(e.target.value)}
              data-testid="operator-user-input"
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Contraseña"
              value={contrasena}
              onChange={e => setContrasena(e.target.value)}
              data-testid="operator-pass-input"
              style={inputStyle}
            />

            {loginError && (
              <p data-testid="operator-login-error" style={{ fontSize: '13px', color: '#E53E3E', textAlign: 'center' }}>
                {loginError}
              </p>
            )}

            <button type="submit" data-testid="operator-login-btn" style={btnPrimary}>
              Ingresar
            </button>

            <button type="button" onClick={onClose} style={{ ...btnSecondary, marginTop: '8px' }}>
              Cancelar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={panelStyle} data-testid="operator-panel">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontWeight: 700, fontSize: '14px', letterSpacing: '0.2em', color: '#0A0A0A' }}>
            ETERNA
          </span>
          <span style={{ fontWeight: 300, fontSize: '13px', color: '#6B6B6B' }}>
            Panel de Operador
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div data-testid="operator-total-counter" style={{
            background: 'rgba(14,165,233,0.1)', padding: '6px 16px',
            fontWeight: 600, fontSize: '13px', color: '#0EA5E9',
            letterSpacing: '0.04em',
          }}>
            {total} registros
          </div>
          <button onClick={fetchEntries} title="Actualizar" style={iconBtn}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
          <button onClick={exportCSV} data-testid="export-csv-btn" title="Exportar CSV" style={iconBtn}>
            <Download size={16} />
          </button>
          <button onClick={handleLogout} data-testid="operator-logout-btn" title="Cerrar sesión" style={iconBtn}>
            <LogOut size={16} />
          </button>
          <button onClick={onClose} style={iconBtn}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.04)', flexShrink: 0 }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            placeholder="Buscar por nombre, correo o ciudad..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="operator-search"
            style={{ ...inputStyle, paddingLeft: '36px', fontSize: '13px', padding: '10px 12px 10px 36px' }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: '#F5F5F7', zIndex: 1 }}>
              {['#', 'Nombre', 'Correo', 'WhatsApp', 'Ciudad', 'Modelo', 'Fecha', 'Status', 'Notas'].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left',
                  fontWeight: 600, fontSize: '11px', letterSpacing: '0.08em',
                  color: '#6B6B6B', borderBottom: '1px solid rgba(0,0,0,0.08)',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(entry => (
              <tr key={entry.id} data-testid={`operator-row-${entry.numero_en_lista}`} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                <td style={cellStyle}>{entry.numero_en_lista}</td>
                <td style={{ ...cellStyle, fontWeight: 500 }}>{entry.nombre}</td>
                <td style={cellStyle}>{entry.correo}</td>
                <td style={cellStyle}>{entry.whatsapp}</td>
                <td style={cellStyle}>{entry.ciudad}</td>
                <td style={cellStyle}>{entry.modelo}</td>
                <td style={{ ...cellStyle, whiteSpace: 'nowrap', fontSize: '12px' }}>
                  {entry.fecha_registro ? new Date(entry.fecha_registro).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                </td>
                <td style={cellStyle}>
                  <select
                    value={entry.status}
                    onChange={e => updateStatus(entry.id, e.target.value)}
                    data-testid={`status-select-${entry.numero_en_lista}`}
                    style={selectStyle(entry.status)}
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="contactado">Contactado</option>
                    <option value="confirmado">Confirmado</option>
                  </select>
                </td>
                <td style={cellStyle}>
                  <input
                    value={entry.notas || ''}
                    onChange={e => updateNotes(entry.id, e.target.value)}
                    placeholder="Agregar nota..."
                    data-testid={`notes-input-${entry.numero_en_lista}`}
                    style={{
                      width: '100%', minWidth: '140px',
                      padding: '6px 8px', fontSize: '12px',
                      border: '1px solid rgba(0,0,0,0.08)',
                      background: 'transparent', outline: 'none',
                      fontFamily: 'Inter, sans-serif', color: '#333',
                    }}
                  />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                  {searchTerm ? 'Sin resultados para esta búsqueda.' : 'No hay registros aún.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '14px 16px',
  background: 'rgba(255,255,255,0.4)',
  border: '1px solid rgba(0,0,0,0.08)',
  fontFamily: 'Inter, sans-serif', fontSize: '14px',
  color: '#0A0A0A', outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary = {
  width: '100%', padding: '14px',
  background: '#0A0A0A', color: '#F5F5F7',
  fontFamily: 'Inter, sans-serif', fontWeight: 600,
  fontSize: '13px', letterSpacing: '0.06em',
  border: 'none', cursor: 'pointer',
};

const btnSecondary = {
  width: '100%', padding: '14px',
  background: 'transparent', color: '#6B6B6B',
  fontFamily: 'Inter, sans-serif', fontWeight: 400,
  fontSize: '13px', border: '1px solid rgba(0,0,0,0.08)',
  cursor: 'pointer',
};

const iconBtn = {
  width: '36px', height: '36px', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  background: 'none', border: '1px solid rgba(0,0,0,0.08)',
  cursor: 'pointer', color: '#333',
};

const cellStyle = {
  padding: '12px 16px', color: '#333',
  fontFamily: 'Inter, sans-serif',
};

const selectStyle = (status) => ({
  padding: '6px 8px', fontSize: '12px',
  fontFamily: 'Inter, sans-serif',
  border: '1px solid rgba(0,0,0,0.08)',
  background: status === 'confirmado' ? 'rgba(34,197,94,0.1)' :
              status === 'contactado' ? 'rgba(14,165,233,0.1)' : 'transparent',
  color: status === 'confirmado' ? '#16a34a' :
         status === 'contactado' ? '#0EA5E9' : '#333',
  outline: 'none', cursor: 'pointer',
});

export default OperatorPanel;
